import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { AnalysisHeaderData, AnalysisPageTabValue } from '@/features/analysisPage/types';
import { mapAnalysisToAnalysisHeader } from '@/shared/api/mappers/analysis';
import {
  useDeleteAnalysis,
  useRenameAnalysis,
  useResetAnalysisNewSellsCounter,
  useStopAnalysis,
} from '@/shared/hooks/analysis/mutations';
import { useGetAnalysisByIdWithCache } from '@/shared/hooks/analysis/useGetAnalysisByIdWithCache';

export const useAnalysisPageData = (analysisId: string) => {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();

  // Déterminer l'onglet actif basé sur les paramètres d'URL
  const activeTab = useMemo((): AnalysisPageTabValue => {
    if (tab === 'niches') return 'niches';
    if (tab === 'history') return 'history';
    return 'statistics'; // Défaut
  }, [tab]);

  // Récupérer les données de l'analyse avec statistiques
  const {
    data: analysisData,
    isLoading: isLoadingAnalysis,
    error: analysisError,
    refetch: refetchAnalysis,
  } = useGetAnalysisByIdWithCache(
    {
      id: analysisId,
    },
    {
      refetchInterval: ({ state }: { state: any }) => {
        const analysis = state.data?.getAnalysisById;
        return analysis?.status === 'starting' || analysis?.status === 'active' ? 30000 : false;
      },
    },
  );

  // Hooks centralisés pour les mutations
  const { renameAnalysis } = useRenameAnalysis();
  const { stopAnalysis } = useStopAnalysis();
  const { deleteAnalysis } = useDeleteAnalysis();
  const { resetAnalysisNewSellsCounter } = useResetAnalysisNewSellsCounter();

  // Gérer la remise à 0 du compteur de nouvelles ventes lorsque l'utilisateur consulte l'analyse
  const analysisHasNewSells = analysisData?.getAnalysisById?.newSells && analysisData.getAnalysisById.newSells > 0;
  useEffect(() => {
    if (!analysisHasNewSells) {
      return;
    }
    // Remettre à zéro après 5 secondes
    setTimeout(() => {
      if (analysisId) {
        resetAnalysisNewSellsCounter(analysisId);
      }
    }, 5000);
    // Cleanup not needed as we want the timeout to run even if the component unmounts
  }, [analysisHasNewSells, analysisId, resetAnalysisNewSellsCounter]);

  // Récupérer les données de l'analyse depuis la query
  const analysisStatsData = analysisData?.getAnalysisById;

  // Mapper les données pour l'en-tête
  const analysisHeaderData = useMemo((): AnalysisHeaderData | null => {
    if (!analysisStatsData) return null;
    return mapAnalysisToAnalysisHeader(analysisStatsData);
  }, [analysisStatsData]);

  // Statistiques de l'analyse
  const analysisStats = useMemo(() => {
    if (!analysisStatsData) return null;
    return {
      analysedProductCount: analysisStatsData.analysedProductCount || 0,
      newSells: analysisStatsData.newSells || 0,
      lastUpdatedAt: analysisStatsData.lastUpdatedAt ? new Date(analysisStatsData.lastUpdatedAt) : null,
    };
  }, [analysisStatsData]);

  const handleRename = async (newName: string) => {
    if (!analysisId || !newName.trim()) return;
    await renameAnalysis(analysisId, newName);
  };

  const handleStop = async () => {
    if (!analysisId) return;
    await stopAnalysis(analysisId);
  };

  const handleDelete = async () => {
    if (!analysisId) return;

    // Déterminer le statut actuel pour la suppression
    const currentStatus = analysisStatsData?.status === 'active' ? 'active' : 'inactive';

    await deleteAnalysis(analysisId, currentStatus);

    // Naviguer vers la page de liste appropriée selon le type
    if (analysisStatsData?.type === 'catalog') {
      navigate('/analysis/products');
    } else if (analysisStatsData?.type === 'profile') {
      navigate('/analysis/vendors');
    } else {
      navigate('/'); // Fallback
    }
  };

  return {
    // État de l'interface
    activeTab,

    // Données de l'analyse
    analysisHeaderData,
    analysisStats,

    // États de chargement et erreurs
    isLoading: isLoadingAnalysis,
    error: analysisError ? "Erreur lors du chargement de l'analyse" : null,

    // Actions
    handleRename,
    handleStop,
    handleDelete,
    refetch: refetchAnalysis,

    // États des mutations
    isUpdating: false, // Les états de chargement sont gérés dans les hooks centralisés
  };
};
